import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, Calendar as CalendarIcon, Clock, MapPin, Trash2, Edit2,
  ChevronLeft, ChevronRight, Repeat, Users, User, ArrowLeft,
  CheckCircle, AlertCircle, Bell, Settings, LogOut, Filter,
  Target, Dumbbell, GraduationCap, Home, Flame, Trophy
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, addMonths, subMonths, addDays, addWeeks, startOfWeek, endOfWeek, isToday } from 'date-fns';

const FamilySchedulePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedAthlete, setSelectedAthlete] = useState('all');
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'

  const [newEvent, setNewEvent] = useState({
    title: '',
    event_type: 'practice',
    start_time: '',
    end_time: '',
    location: '',
    description: '',
    athlete_id: 'family', // 'family' for family-wide event, or athlete user_id
    is_recurring: false,
    recurrence_days: [],
    recurrence_end_date: '',
    requires_parent: false, // Does parent need to attend/drive?
    reminder: true
  });

  const eventTypes = [
    { value: 'practice', label: 'Team Practice', color: 'bg-aura-primary', icon: Target },
    { value: 'game', label: 'Game/Match', color: 'bg-green-500', icon: Trophy },
    { value: 'personal_training', label: 'Personal Training', color: 'bg-purple-500', icon: Dumbbell },
    { value: 'conditioning', label: 'Conditioning', color: 'bg-orange-500', icon: Flame },
    { value: 'workout', label: 'Home Workout', color: 'bg-aura-secondary', icon: Dumbbell },
    { value: 'school', label: 'School Event', color: 'bg-blue-500', icon: GraduationCap },
    { value: 'family', label: 'Family Event', color: 'bg-pink-500', icon: Home },
    { value: 'recovery', label: 'Recovery Day', color: 'bg-cyan-500', icon: Target },
    { value: 'appointment', label: 'Appointment', color: 'bg-amber-500', icon: CalendarIcon },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch linked athletes
      const athletesRes = await axios.get(`${API}/family/athletes`, { headers });
      setAthletes(athletesRes.data.athletes || []);

      // Fetch all family events (parent's events + all athlete events)
      const eventsRes = await axios.get(`${API}/family/schedule`, { headers });
      setAllEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      // Use empty defaults
      setAthletes([]);
      setAllEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('aura_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Convert 'family' back to empty string for API
      const eventData = {
        ...newEvent,
        athlete_id: newEvent.athlete_id === 'family' ? '' : newEvent.athlete_id
      };

      if (eventData.is_recurring && eventData.recurrence_days.length > 0) {
        // Create recurring events
        const startDate = new Date(eventData.start_time);
        const endRecurrence = eventData.recurrence_end_date 
          ? new Date(eventData.recurrence_end_date) 
          : addWeeks(startDate, 12);
        
        const dayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
        const selectedDays = eventData.recurrence_days.map(d => dayMap[d]);
        const events = [];
        let currentDate = startDate;

        while (currentDate <= endRecurrence) {
          if (selectedDays.includes(currentDate.getDay())) {
            const eventStart = new Date(currentDate);
            const originalStart = new Date(eventData.start_time);
            const originalEnd = new Date(eventData.end_time);
            
            eventStart.setHours(originalStart.getHours(), originalStart.getMinutes());
            const eventEnd = new Date(currentDate);
            eventEnd.setHours(originalEnd.getHours(), originalEnd.getMinutes());

            events.push({
              ...eventData,
              start_time: eventStart.toISOString(),
              end_time: eventEnd.toISOString(),
              is_recurring: true,
              recurrence_group: `rec_${Date.now()}`
            });
          }
          currentDate = addDays(currentDate, 1);
        }

        for (const event of events) {
          await axios.post(`${API}/family/schedule`, event, { headers });
        }
        toast.success(`Created ${events.length} recurring events!`);
      } else {
        await axios.post(`${API}/family/schedule`, eventData, { headers });
        toast.success('Event created!');
      }

      setShowNewDialog(false);
      resetNewEvent();
      fetchData();
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    if (!editingEvent) return;

    try {
      const token = localStorage.getItem('aura_token');
      await axios.put(`${API}/family/schedule/${editingEvent.event_id}`, editingEvent, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event updated!');
      setShowEditDialog(false);
      setEditingEvent(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Delete this event?')) return;
    try {
      const token = localStorage.getItem('aura_token');
      await axios.delete(`${API}/family/schedule/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const resetNewEvent = () => {
    setNewEvent({
      title: '',
      event_type: 'practice',
      start_time: '',
      end_time: '',
      location: '',
      description: '',
      athlete_id: 'family',
      is_recurring: false,
      recurrence_days: [],
      recurrence_end_date: '',
      requires_parent: false,
      reminder: true
    });
  };

  const openEditDialog = (event) => {
    setEditingEvent({...event});
    setShowEditDialog(true);
  };

  const getEventTypeInfo = (type) => {
    return eventTypes.find(t => t.value === type) || eventTypes[0];
  };

  const getAthleteById = (id) => {
    return athletes.find(a => a.user_id === id);
  };

  const getEventsForDate = (date) => {
    return allEvents.filter(event => {
      const eventDate = parseISO(event.start_time);
      const matchesDate = isSameDay(eventDate, date);
      const matchesAthlete = selectedAthlete === 'all' || 
        event.athlete_id === selectedAthlete || 
        !event.athlete_id;
      return matchesDate && matchesAthlete;
    });
  };

  const filteredEvents = allEvents.filter(event => {
    return selectedAthlete === 'all' || 
      event.athlete_id === selectedAthlete || 
      !event.athlete_id;
  });

  const upcomingEvents = filteredEvents
    .filter(e => new Date(e.start_time) >= new Date())
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

  const selectedDateEvents = getEventsForDate(selectedDate);
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get today's responsibilities (tasks that need athlete attention)
  const todayTasks = upcomingEvents.filter(e => {
    const eventDate = new Date(e.start_time);
    const today = new Date();
    return eventDate.toDateString() === today.toDateString();
  });

  const thisWeekTasks = upcomingEvents.filter(e => {
    const eventDate = new Date(e.start_time);
    const weekEnd = endOfWeek(new Date());
    return eventDate <= weekEnd;
  });

  return (
    <div className="min-h-screen bg-aura-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-aura-bg/95 backdrop-blur-lg border-b border-white/5">
        <div className="flex items-center justify-between h-14 px-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/supporter')}
              className="p-2 text-aura-text-muted hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-8 h-8 bg-aura-secondary rounded-sm flex items-center justify-center">
              <span className="font-heading font-black text-white text-lg">A</span>
            </div>
            <span className="font-heading font-bold text-lg text-white tracking-tight">AURA</span>
            <span className="text-xs text-aura-text-muted uppercase tracking-wider hidden sm:block">Family Schedule</span>
          </div>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-aura-secondary flex items-center justify-center">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <span className="font-heading font-bold text-white text-sm">
                        {user?.name?.charAt(0)?.toUpperCase() || 'S'}
                      </span>
                    )}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-aura-surface border-white/10">
                <div className="px-3 py-2">
                  <p className="font-medium text-white">{user?.name}</p>
                  <p className="text-sm text-aura-text-muted">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem asChild>
                  <Link to="/supporter" className="flex items-center gap-2 text-white cursor-pointer">
                    <Users className="w-4 h-4" />
                    Family Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="flex items-center gap-2 text-white cursor-pointer">
                    <Settings className="w-4 h-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-aura-destructive cursor-pointer"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Header with Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
              Family Schedule
            </h1>
            <p className="text-aura-text-muted mt-1">
              Manage all athlete and family events in one place
            </p>
          </div>
          <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
            <DialogTrigger asChild>
              <Button className="bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider" data-testid="add-family-event-btn">
                <Plus className="w-5 h-5 mr-2" />
                Add Event
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-aura-surface border-white/10 max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                  New Event
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateEvent} className="space-y-4 mt-4">
                {/* Athlete Selection */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">For Athlete</Label>
                  <Select value={newEvent.athlete_id || 'family'} onValueChange={(v) => setNewEvent({ ...newEvent, athlete_id: v === 'family' ? '' : v })}>
                    <SelectTrigger className="h-12 bg-aura-bg border-white/10 text-white">
                      <SelectValue placeholder="Select athlete (or family event)" />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      <SelectItem value="family" className="text-white">
                        <div className="flex items-center gap-2">
                          <Home className="w-4 h-4" />
                          Family Event (All)
                        </div>
                      </SelectItem>
                      {athletes.map((athlete) => (
                        <SelectItem key={athlete.user_id} value={athlete.user_id} className="text-white">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            {athlete.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Event Title */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Event Title</Label>
                  <Input
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    placeholder="e.g., Team Practice, Personal Training"
                    required
                    className="h-12 bg-aura-bg border-white/10 text-white"
                    data-testid="family-event-title-input"
                  />
                </div>

                {/* Event Type */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Event Type</Label>
                  <Select value={newEvent.event_type} onValueChange={(v) => setNewEvent({ ...newEvent, event_type: v })}>
                    <SelectTrigger className="h-12 bg-aura-bg border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      {eventTypes.map((type) => {
                        const Icon = type.icon;
                        return (
                          <SelectItem key={type.value} value={type.value} className="text-white">
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${type.color}`} />
                              {type.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date/Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Start Time</Label>
                    <Input
                      type="datetime-local"
                      value={newEvent.start_time}
                      onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                      required
                      className="h-12 bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">End Time</Label>
                    <Input
                      type="datetime-local"
                      value={newEvent.end_time}
                      onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                      required
                      className="h-12 bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Location</Label>
                  <Input
                    value={newEvent.location}
                    onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                    placeholder="e.g., Main Field, Gold's Gym, School"
                    className="h-12 bg-aura-bg border-white/10 text-white"
                  />
                </div>

                {/* Parent Required Toggle */}
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-aura-secondary" />
                    <div>
                      <p className="text-white text-sm font-medium">Parent Required</p>
                      <p className="text-xs text-aura-text-muted">Need to drive or attend?</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewEvent({ ...newEvent, requires_parent: !newEvent.requires_parent })}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      newEvent.requires_parent ? 'bg-aura-secondary' : 'bg-white/20'
                    }`}
                  >
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      newEvent.requires_parent ? 'translate-x-7' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Notes</Label>
                  <Textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    placeholder="Any additional details..."
                    className="bg-aura-bg border-white/10 text-white min-h-[80px]"
                  />
                </div>

                {/* Recurring Section */}
                <div className="space-y-3 pt-2 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Repeat className="w-4 h-4 text-aura-secondary" />
                      <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Recurring Event</Label>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNewEvent({ ...newEvent, is_recurring: !newEvent.is_recurring })}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        newEvent.is_recurring ? 'bg-aura-secondary' : 'bg-white/20'
                      }`}
                    >
                      <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        newEvent.is_recurring ? 'translate-x-7' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  {newEvent.is_recurring && (
                    <div className="space-y-3 p-3 bg-aura-secondary/10 rounded-lg border border-aura-secondary/30">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Repeat On</Label>
                        <div className="flex flex-wrap gap-2">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                            <button
                              key={day}
                              type="button"
                              onClick={() => {
                                const days = newEvent.recurrence_days.includes(day)
                                  ? newEvent.recurrence_days.filter(d => d !== day)
                                  : [...newEvent.recurrence_days, day];
                                setNewEvent({ ...newEvent, recurrence_days: days });
                              }}
                              className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                                newEvent.recurrence_days.includes(day)
                                  ? 'bg-aura-secondary text-white'
                                  : 'bg-white/10 text-aura-text-muted hover:bg-white/20'
                              }`}
                            >
                              {day}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Repeat Until</Label>
                        <Input
                          type="date"
                          value={newEvent.recurrence_end_date}
                          onChange={(e) => setNewEvent({ ...newEvent, recurrence_end_date: e.target.value })}
                          className="h-10 bg-aura-bg border-white/10 text-white"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider h-12">
                  {newEvent.is_recurring && newEvent.recurrence_days.length > 0 
                    ? 'Create Recurring Events' 
                    : 'Create Event'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Athlete Filter */}
        <div className="flex items-center gap-3 overflow-x-auto pb-2">
          <button
            onClick={() => setSelectedAthlete('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              selectedAthlete === 'all'
                ? 'bg-aura-secondary text-white'
                : 'bg-white/10 text-aura-text-muted hover:bg-white/20'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            All Family
          </button>
          {athletes.map((athlete) => (
            <button
              key={athlete.user_id}
              onClick={() => setSelectedAthlete(athlete.user_id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedAthlete === athlete.user_id
                  ? 'bg-aura-primary text-white'
                  : 'bg-white/10 text-aura-text-muted hover:bg-white/20'
              }`}
            >
              <User className="w-4 h-4 inline mr-2" />
              {athlete.name}
            </button>
          ))}
        </div>

        {/* Today's Responsibilities Card */}
        {todayTasks.length > 0 && (
          <Card className="bg-gradient-to-r from-aura-primary/20 to-aura-secondary/20 border-aura-primary/30">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-aura-primary" />
                Today's Schedule ({todayTasks.length} events)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {todayTasks.map((event) => {
                  const typeInfo = getEventTypeInfo(event.event_type);
                  const athlete = getAthleteById(event.athlete_id);
                  return (
                    <div key={event.event_id} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <div className={`w-10 h-10 rounded-lg ${typeInfo.color} bg-opacity-20 flex items-center justify-center`}>
                        {React.createElement(typeInfo.icon, { className: `w-5 h-5 text-white` })}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-white truncate">{event.title}</p>
                          {event.requires_parent && (
                            <Badge className="bg-aura-secondary/20 text-aura-secondary border-0 text-xs">
                              Parent Required
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-aura-text-muted">
                          {format(parseISO(event.start_time), 'h:mm a')} 
                          {athlete ? ` • ${athlete.name}` : ''}
                          {event.location ? ` • ${event.location}` : ''}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Calendar Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="text-aura-text-muted hover:text-white"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="text-center">
            <h2 className="font-heading font-bold text-xl text-white uppercase tracking-tight">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="text-aura-text-muted hover:text-white"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Calendar Grid */}
        <Card className="bg-aura-surface border-white/10">
          <CardContent className="p-4">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="text-center text-xs text-aura-text-muted uppercase tracking-wider py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: monthStart.getDay() }).map((_, i) => (
                <div key={`empty-${i}`} className="aspect-square" />
              ))}
              
              {daysInMonth.map((day) => {
                const dayEvents = getEventsForDate(day);
                const isSelected = isSameDay(day, selectedDate);
                const hasParentRequired = dayEvents.some(e => e.requires_parent);

                return (
                  <button
                    key={day.toISOString()}
                    onClick={() => setSelectedDate(day)}
                    className={`aspect-square p-1 rounded-lg transition-colors relative ${
                      isSelected
                        ? 'bg-aura-primary'
                        : isToday(day)
                        ? 'bg-white/10'
                        : 'hover:bg-white/5'
                    }`}
                  >
                    <span className={`text-sm font-medium ${
                      isSelected ? 'text-white' : isToday(day) ? 'text-aura-primary' : 'text-white'
                    }`}>
                      {format(day, 'd')}
                    </span>
                    {dayEvents.length > 0 && (
                      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                        {dayEvents.slice(0, 3).map((event, i) => (
                          <div
                            key={i}
                            className={`w-1.5 h-1.5 rounded-full ${getEventTypeInfo(event.event_type).color}`}
                          />
                        ))}
                      </div>
                    )}
                    {hasParentRequired && (
                      <div className="absolute top-1 right-1 w-2 h-2 bg-aura-secondary rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Selected Date Events */}
        <div>
          <h3 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-4">
            {format(selectedDate, 'EEEE, MMMM d')}
          </h3>
          
          {selectedDateEvents.length > 0 ? (
            <div className="space-y-3">
              {selectedDateEvents.map((event) => {
                const typeInfo = getEventTypeInfo(event.event_type);
                const athlete = getAthleteById(event.athlete_id);
                const Icon = typeInfo.icon;
                
                return (
                  <Card key={event.event_id} className="bg-aura-surface border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`w-1 h-full min-h-[60px] rounded-full ${typeInfo.color}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-medium text-white">{event.title}</h4>
                              <Badge variant="outline" className="text-xs capitalize">
                                {typeInfo.label}
                              </Badge>
                              {athlete && (
                                <Badge className="bg-aura-primary/20 text-aura-primary border-0 text-xs">
                                  {athlete.name}
                                </Badge>
                              )}
                              {event.requires_parent && (
                                <Badge className="bg-aura-secondary/20 text-aura-secondary border-0 text-xs">
                                  <Users className="w-3 h-3 mr-1" />
                                  Parent Required
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-sm text-aura-text-muted">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {format(parseISO(event.start_time), 'h:mm a')} - {format(parseISO(event.end_time), 'h:mm a')}
                              </span>
                              {event.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {event.location}
                                </span>
                              )}
                            </div>
                            {event.description && (
                              <p className="text-sm text-aura-text-subtle mt-2">{event.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(event)}
                            className="text-aura-text-muted hover:text-white"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteEvent(event.event_id)}
                            className="text-aura-text-muted hover:text-aura-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-8 text-center">
                <CalendarIcon className="w-12 h-12 text-aura-text-subtle mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg text-white mb-2">No Events</h3>
                <p className="text-aura-text-muted mb-4">Nothing scheduled for this day</p>
                <Button 
                  onClick={() => setShowNewDialog(true)}
                  className="bg-aura-primary hover:bg-aura-primary-hover text-white"
                >
                  Add Event
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* This Week Overview */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
              This Week ({thisWeekTasks.length} events)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {thisWeekTasks.length > 0 ? (
              <div className="space-y-2">
                {thisWeekTasks.slice(0, 7).map((event) => {
                  const typeInfo = getEventTypeInfo(event.event_type);
                  const athlete = getAthleteById(event.athlete_id);
                  return (
                    <div key={event.event_id} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <div className={`w-10 h-10 rounded-lg ${typeInfo.color} bg-opacity-20 flex items-center justify-center flex-shrink-0`}>
                        {React.createElement(typeInfo.icon, { className: `w-5 h-5 text-white` })}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white truncate">{event.title}</p>
                        <p className="text-sm text-aura-text-muted">
                          {format(parseISO(event.start_time), 'EEE, MMM d • h:mm a')}
                          {athlete ? ` • ${athlete.name}` : ''}
                        </p>
                      </div>
                      {event.requires_parent && (
                        <Badge className="bg-aura-secondary/20 text-aura-secondary border-0 text-xs flex-shrink-0">
                          <Users className="w-3 h-3" />
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-aura-text-muted text-center py-4">No events this week</p>
            )}
          </CardContent>
        </Card>

        {/* Edit Event Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-aura-surface border-white/10 max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                Edit Event
              </DialogTitle>
            </DialogHeader>
            {editingEvent && (
              <form onSubmit={handleUpdateEvent} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Event Title</Label>
                  <Input
                    value={editingEvent.title}
                    onChange={(e) => setEditingEvent({ ...editingEvent, title: e.target.value })}
                    required
                    className="h-12 bg-aura-bg border-white/10 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Event Type</Label>
                  <Select value={editingEvent.event_type} onValueChange={(v) => setEditingEvent({ ...editingEvent, event_type: v })}>
                    <SelectTrigger className="h-12 bg-aura-bg border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      {eventTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value} className="text-white">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${type.color}`} />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Start Time</Label>
                    <Input
                      type="datetime-local"
                      value={editingEvent.start_time?.slice(0, 16)}
                      onChange={(e) => setEditingEvent({ ...editingEvent, start_time: e.target.value })}
                      required
                      className="h-12 bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">End Time</Label>
                    <Input
                      type="datetime-local"
                      value={editingEvent.end_time?.slice(0, 16)}
                      onChange={(e) => setEditingEvent({ ...editingEvent, end_time: e.target.value })}
                      required
                      className="h-12 bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Location</Label>
                  <Input
                    value={editingEvent.location || ''}
                    onChange={(e) => setEditingEvent({ ...editingEvent, location: e.target.value })}
                    className="h-12 bg-aura-bg border-white/10 text-white"
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-aura-secondary" />
                    <div>
                      <p className="text-white text-sm font-medium">Parent Required</p>
                      <p className="text-xs text-aura-text-muted">Need to drive or attend?</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditingEvent({ ...editingEvent, requires_parent: !editingEvent.requires_parent })}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      editingEvent.requires_parent ? 'bg-aura-secondary' : 'bg-white/20'
                    }`}
                  >
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      editingEvent.requires_parent ? 'translate-x-7' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Notes</Label>
                  <Textarea
                    value={editingEvent.description || ''}
                    onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })}
                    className="bg-aura-bg border-white/10 text-white min-h-[80px]"
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider h-12">
                    Save Changes
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setShowEditDialog(false)}
                    className="border-white/20 text-white"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default FamilySchedulePage;
